/**
 * The calorie ring — the drawing half. The arithmetic is `ringModel` in
 * `@calorio/core`, shared with the phone so the two can't disagree about what
 * a day looks like, and tested there.
 *
 * From the spec: *"Ring is calories remaining, split into protein/carbs/fat
 * arcs for what has been eaten."*
 */

import { ringModel, type MacroKey, type Macros } from "@calorio/core";

interface RingProps {
  consumed: Macros;
  targetKcal: number | null;
  size?: number;
}

const TRACK_WIDTH = 14;
/** Start at the top rather than 3 o'clock, where a clockwise arc reads as progress. */
const ROTATION = -90;

const STROKE: Record<MacroKey, string> = {
  protein: "var(--protein)",
  carbs: "var(--carbs)",
  fat: "var(--fat)",
};

export function Ring({ consumed, targetKcal, size = 232 }: RingProps) {
  const radius = (size - TRACK_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const ring = ringModel(consumed, targetKcal);

  // Over target: one alert-coloured ring, drawn full, rather than the macro
  // split — the overshoot is the headline and detail would bury it.
  const drawn = ring.over
    ? [{ color: "var(--alert)", length: circumference, start: 0 }]
    : ring.arcs.map((arc) => ({
        color: STROKE[arc.macro],
        length: arc.length * circumference,
        start: arc.start * circumference,
      }));

  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <g transform={`rotate(${ROTATION} ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--surface-sunken)"
            strokeWidth={TRACK_WIDTH}
          />
          {drawn.map((arc, index) => (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={TRACK_WIDTH}
              strokeLinecap="butt"
              strokeDasharray={`${arc.length} ${circumference - arc.length}`}
              strokeDashoffset={-arc.start}
            />
          ))}
        </g>
      </svg>

      <div className="ring__center">
        <div className={`ring__value num${ring.over ? " ring__value--over" : ""}`}>
          {Math.round(ring.headline).toLocaleString()}
        </div>
        <div className="ring__label">{ring.label}</div>
        {ring.ratio && (
          <div className="ring__ratio num">
            {Math.round(ring.ratio.eaten).toLocaleString()} /{" "}
            {Math.round(ring.ratio.target).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}
